// SPEC-15 · the new-password screen, opened by the link of the reset email.
// Firebase appends `mode` and `oobCode` to the action URL the email template
// points at (poweryourflow-ops/HANDOFF.md, step F7); the code is the whole
// proof, so the screen needs no session. Any other email link passes on to
// Firebase's own page (src/account/actionLink.ts).
//
// The rule is Criar conta's: 8 characters and a digit. Firebase says
// whether the code still holds (1 hour, one use) only when it is sent.

import { useEffect, useState } from "react";
import type { FormEvent } from "react";

import { readActionLink } from "../account/actionLink";
import { clearAttempts } from "../account/attempts";
import { SIGN_IN_TIMEOUT_MS, SignInError } from "../account/signIn";
import type { SignIn, SignInFailure } from "../account/signIn";
import { passwordMeetsRule } from "../account/signUpForm";
import { Field } from "../components/Field";
import type { Copy } from "../i18n/language";
import { useCopy } from "../i18n/LanguageProvider";
import { Link, paths } from "../lib/router";
import { within } from "../lib/within";

type ErrorKey = keyof Copy["newPassword"]["errors"];
type Step = "form" | "done" | "invalid";

const button =
  "flex h-[var(--control-h)] w-full items-center justify-center rounded-pill text-body font-semibold no-underline " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";

export function NewPassword({ signIn }: { signIn: SignIn }) {
  const [link] = useState(() => readActionLink(window.location.search, import.meta.env.VITE_FIREBASE_AUTH_DOMAIN));
  const code = link.kind === "reset" ? link.code : null;
  const [step, setStep] = useState<Step>(code ? "form" : "invalid");
  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState(false);
  const [banner, setBanner] = useState<ErrorKey | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (link.kind === "firebase") {
      window.location.replace(link.url);
    } else if (link.kind === "reset") {
      // The code is held in memory: it leaves the address bar and the
      // history, so back on a shared phone cannot reopen the form.
      window.history.replaceState(window.history.state, "", paths.newPassword);
    }
  }, [link]);

  const text = useCopy().newPassword;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || password === "" || !code) return;
    setBanner(null);
    if (!passwordMeetsRule(password)) {
      setFieldError(true);
      return;
    }
    setFieldError(false);
    setSubmitting(true);
    try {
      await within(signIn.confirmPasswordReset(code, password), SIGN_IN_TIMEOUT_MS);
      // A password just set deserves five fresh tries on Entrar.
      clearAttempts();
      setStep("done");
    } catch (error) {
      let failure: SignInFailure = "offline";
      if (error instanceof SignInError) {
        failure = error.failure;
      } else {
        console.error("[new-password] no answer after the wait", { kind: error instanceof Error ? error.name : typeof error });
      }
      if (failure === "link-invalid") setStep("invalid");
      else if (failure === "weak-password") setFieldError(true);
      else setBanner(failure === "offline" ? "offline" : "failed");
    }
    setSubmitting(false);
  };

  const heading = (title: string, lead: string) => (
    <>
      <h1 className="pt-6 text-display leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)]">
        {title}
      </h1>
      <p className="pt-3 text-body leading-[var(--lh-body)] text-muted">{lead}</p>
    </>
  );

  // On its way to Firebase's page: nothing to show for the instant it takes.
  if (link.kind === "firebase") return null;

  return (
    <main className="screen-enter flex min-h-dvh flex-col bg-cream px-5 pt-5">
      <header className="flex items-center">
        <Link
          to={paths.signIn}
          className="flex size-[var(--touch-min)] items-center justify-center rounded-pill bg-violet text-title text-cream no-underline hover:bg-violet-ink hover:text-cream"
        >
          <span aria-hidden="true">←</span>
          <span className="sr-only">{text.back}</span>
        </Link>
      </header>

      {step === "form" ? (
        <form noValidate onSubmit={submit} className="flex flex-1 flex-col">
          {heading(text.title, text.lead)}
          <div className="pt-8">
            <Field
              label={text.label}
              placeholder={text.placeholder}
              hint={text.hint}
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              error={fieldError ? text.errors.passwordInvalid : null}
            />
          </div>
          <div className="mt-auto flex flex-col gap-3 pt-8 pb-6">
            <button
              type="submit"
              disabled={submitting || password === ""}
              className={`${button} bg-dark text-cream hover:bg-dark-hover disabled:bg-line-10 disabled:text-disabled`}
            >
              {submitting ? text.submitting : text.submit}
            </button>
            {banner && (
              <p role="alert" className="rounded-card-inner bg-orange-tint px-4 py-3 text-center text-body-sm text-orange-ink">
                {text.errors[banner]}
              </p>
            )}
          </div>
        </form>
      ) : (
        <div className="flex flex-1 flex-col">
          {step === "done" ? heading(text.doneTitle, text.doneLead) : heading(text.invalidTitle, text.invalidLead)}
          <div className="mt-auto pt-8 pb-6">
            <Link to={paths.signIn} className={`${button} bg-dark text-cream hover:bg-dark-hover hover:text-cream`}>
              {text.signIn}
            </Link>
          </div>
        </div>
      )}
    </main>
  );
}
